import { useState, useEffect, useRef } from 'react';
import { HubConnectionBuilder, HttpTransportType } from '@microsoft/signalr';

/**
 * Manages the SignalR lifecycle for /HubNovaEngineTelevisor.
 *
 * Listens to event: RecibirDatos (Dictionary<string, object>)
 *   { Conteo: 47 }                         → producido corrida actual
 *   { Llamadas: [...LlamadaDTOTelevisor] }  → snapshot completo de llamadas activas
 *   { estatus: 2, nombre: "Setup" }         → cambio de estatus de la línea
 *
 * Hub methods:
 *   SuscribirseALinea(linea: string)   — al conectar / reconectar
 *   DesuscribirseALinea(linea: string) — al desmontar
 */
export default function useTelevisorHub(linea) {
    const [tvStatus, setTvStatus]       = useState(null);
    const [tvLlamadas, setTvLlamadas]   = useState(null); // null = aún no recibido del hub
    const [tvConteo, setTvConteo]       = useState(null);
    const [isConnected, setIsConnected] = useState(false);

    const connectionRef = useRef(null);

    useEffect(() => {
        if (!linea) return;

        if (process.env.REACT_APP_ALLOW_WEBSOCKETS === 'false') return;

        const apiBase = process.env.REACT_APP_SERVICE_ENDPOINT_NE || '';
        const hubUrl  = apiBase.replace('/api', '/HubNovaEngineTelevisor');
        if (!hubUrl) return;

        const connection = new HubConnectionBuilder()
            .withUrl(hubUrl, {
                skipNegotiation: true,
                transport: HttpTransportType.WebSockets,
            })
            .withAutomaticReconnect()
            .build();

        connectionRef.current = connection;

        connection.on('RecibirDatos', (data) => {
            if (!data || typeof data !== 'object') return;
            if ('Conteo' in data) {
                setTvConteo(data.Conteo);
            } else if ('Llamadas' in data && Array.isArray(data.Llamadas)) {
                setTvLlamadas(data.Llamadas);
            } else if ('estatus' in data && 'nombre' in data) {
                setTvStatus({ codigo: data.estatus, nombre: data.nombre });
            }
        });

        const subscribe = () =>
            connection.invoke('SuscribirseALinea', linea.toString()).catch(() => {});

        connection.onreconnected(() => {
            subscribe();
            setIsConnected(true);
        });
        connection.onclose(() => setIsConnected(false));
        connection.onreconnecting(() => setIsConnected(false));

        connection
            .start()
            .then(() => {
                setIsConnected(true);
                return subscribe();
            })
            .catch(() => setIsConnected(false));

        return () => {
            connection
                .invoke('DesuscribirseALinea', linea.toString())
                .catch(() => {})
                .finally(() => {
                    connection.stop();
                    connectionRef.current = null;
                });
        };
    }, [linea]);

    return { tvStatus, tvLlamadas, tvConteo, isConnected };
}
